'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import {
  updateCustomerB2BInformationRequest,
  updateCustomerRequest,
  updateCustomerNotificationRequest,
  updateCustomerMarketingConsentRequest,
  updateMetafields,
  updateCompanyLocationRequest,
  updateCompanyLocationAssignAddressRequest,
  addTags,
  updateCompanyRequest,
} from '@/app/actions/customer.actions'
import { CUSTOMER_QUERY_KEY } from '@/context/customer/customer.constants'

import type { CustomerB2BInformationInput } from '@/context/customer/customer.types'
import type { MetafieldsSetInput, CustomerInput, CompanyLocationUpdateInput, CompanyAddressInput, CompanyInput } from '@/types/shopify/admin/admin.types'

// These can be referenced when using the useMutationState when doing batch mutations
// https://tanstack.com/query/v5/docs/framework/react/reference/useMutationState
export const MUTATION_KEYS = {
  UPDATE_CUSTOMER: ['customer', 'update'],
  UPDATE_CUSTOMER_B2B_INFORMATION: ['customer', 'b2b-info'],
  UPDATE_CUSTOMER_NOTIFICATION_METAFIELD: ['customer', 'notification-metafield'],
  UPDATE_CUSTOMER_MARKETING_CONSENT: ['customer', 'marketing-consent'],
  UPDATE_COMPANY: ['company', 'update'],
  UPDATE_COMPANY_LOCATION: ['company-location', 'update'],
  UPDATE_COMPANY_LOCATION_ADDRESS: ['company-location', 'update-address'],
  UPDATE_METAFIELDS: ['customer', 'metafields'],
  ADD_TAGS: ['customer', 'tags'],
} as const

/**
 * useCustomerMutations
 *
 * Centralised collection of customer-related mutations.
 *
 * Responsibilities:
 * - Wraps all customer / company mutations behind a single hook
 * - Automatically invalidates the customer query on success
 *
 * Requirements:
 * - `customerId` and `customerAccessToken` must be provided
 * - Intended for authenticated customer contexts only
 *
 * Side effects:
 * - Invalidates `[CUSTOMER_QUERY_KEY, customerAccessToken]` after every successful mutation
 *
 * Errors:
 * - Throws if `customerId` or `customerAccessToken` is missing
 *
 * Example usage:
 *
 * const { updateCustomer } = useCustomerMutations(customerId, accessToken)
 *
 * updateCustomer.mutate(
 *   {
 *     firstName: 'Elliott',
 *     lastName: 'Smith',
 *     phone: '0412345678',
 *   },
 *   {
 *     onError: (error) => {
 *       console.error(error)
 *     },
 *   }
 * )
 *
 *
 * @param customerId Shopify Customer ID
 * @param customerAccessToken Customer access token used for cache scoping
 */
export function useCustomerMutations(customerId?: string, customerAccessToken?: string) {
  const queryClient = useQueryClient()

  const invalidateCustomer = useCallback(() => {
    if (!customerAccessToken) return

    queryClient.invalidateQueries({
      queryKey: [...CUSTOMER_QUERY_KEY, customerAccessToken],
    })
  }, [queryClient, customerAccessToken])

  const isEnabled = !!customerId && !!customerAccessToken

  return {
    updateCustomer: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_CUSTOMER,
      mutationFn: (input: CustomerInput) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCustomerRequest(customerId, input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateB2BInfo: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_CUSTOMER_B2B_INFORMATION,
      mutationFn: (input: CustomerB2BInformationInput) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCustomerB2BInformationRequest(customerId, input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateNotificationMetafield: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_CUSTOMER_NOTIFICATION_METAFIELD,
      mutationFn: (input: string[]) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCustomerNotificationRequest(customerId, input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateMarketingConsent: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_CUSTOMER_MARKETING_CONSENT,
      mutationFn: (input: { type: 'email' | 'sms'; marketingState: boolean }) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCustomerMarketingConsentRequest(customerId, input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateCompany: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_COMPANY,
      mutationFn: (input: { companyId: string; input: CompanyInput }) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCompanyRequest(input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateCompanyLocation: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_COMPANY_LOCATION,
      mutationFn: (input: { locationId: string; input: CompanyLocationUpdateInput }) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCompanyLocationRequest(input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateCompanyLocationAddress: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_COMPANY_LOCATION_ADDRESS,
      mutationFn: (input: { locationId: string; address: CompanyAddressInput }) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateCompanyLocationAssignAddressRequest(input)
      },
      onSuccess: invalidateCustomer,
    }),

    updateMetafields: useMutation({
      mutationKey: MUTATION_KEYS.UPDATE_METAFIELDS,
      mutationFn: (input: MetafieldsSetInput[]) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        return updateMetafields(input)
      },
      onSuccess: invalidateCustomer,
    }),

    addTags: useMutation({
      mutationKey: MUTATION_KEYS.ADD_TAGS,
      mutationFn: (tags: string[]) => {
        if (!isEnabled) throw new Error('Customer ID or Customer Access Token not found')

        const input = {
          id: customerId,
          tags,
        }

        return addTags(input)
      },
      onSuccess: invalidateCustomer,
    }),
  }
}
